import React, { useEffect, useState } from 'react';
import { getPlans, togglePlanStatus, setDefaultPlan, Plan } from '../services/planService';
import { toast } from 'react-toastify';

interface PlanListProps {
  onEdit?: (plan: Plan) => void;
}

const PlanList: React.FC<PlanListProps> = ({ onEdit }) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPlans();
      setPlans(data);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (plan: Plan) => {
    if (!window.confirm(`Are you sure you want to ${plan.isActive ? 'deactivate' : 'activate'} this plan?`)) return;
    setActionId(plan.planId);
    try {
      await togglePlanStatus(plan.planId);
      toast.success('Plan status updated');
      load();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setActionId(null);
    }
  };

  const handleDefault = async (plan: Plan) => {
    if (!window.confirm('Set this plan as default? Others will be unset.')) return;
    setActionId(plan.planId);
    try {
      await setDefaultPlan(plan.planId);
      toast.success('Default plan updated');
      load();
    } catch {
      toast.error('Failed to set default');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead>
              <tr className="bg-background text-secondary">
                <th className="px-4 py-2">Plan Name</th>
                <th className="px-4 py-2">Price</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2">Default</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.planId} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-semibold flex items-center gap-2">
                    {p.planName}
                    {p.isDefault && (<span className="ml-2 px-2 py-0.5 rounded bg-primary text-white text-xs">Default</span>)}
                    {p.price === 0 && (<span className="ml-2 px-2 py-0.5 rounded bg-green-200 text-green-800 text-xs">Free</span>)}
                  </td>
                  <td className="px-4 py-2">₹{p.price.toFixed(2)}</td>
                  <td className="px-4 py-2">{p.durationInDays} days</td>
                  <td className="px-4 py-2">{p.isActive ? <span className="text-green-600 font-bold">Active</span> : <span className="text-gray-400">Inactive</span>}</td>
                  <td className="px-4 py-2 text-center">
                    {p.isDefault ? 'Yes' : (
                      <button className="text-xs px-2 py-1 bg-background border border-primary text-primary rounded hover:bg-primary hover:text-white"
                        disabled={actionId === p.planId}
                        onClick={() => handleDefault(p)}>Set Default</button>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{new Date(p.createdDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2 flex gap-2">
                    <button className="px-2 py-1 bg-secondary text-white rounded hover:bg-primary transition text-xs" onClick={() => onEdit && onEdit(p)}>Edit</button>
                    <button className={`px-2 py-1 rounded text-xs border ${p.isActive ? 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' : 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200'}`}
                      onClick={() => handleToggle(p)} disabled={actionId === p.planId}>{p.isActive ? 'Deactivate' : 'Activate'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlanList;
